import React, { useState, useEffect, useRef } from 'react';
import BRAND from '../brand';
import { FieldIcon, StatusIcon, HeroIcon, SectionLoader } from './NavIcons';
import { useLegalLinks } from '../legal';
import { api, getTelegramUser, fetchWebUser } from '../api';
import { openLink as platformOpenLink, getDevicePlatform, getPlatform, canOpenInvoice } from '../platform';
import { payViaTgInvoice } from '../payments';
import { getStoredYclid, getTouchToken, getWebClientId, maxBotUrl, tgBotUrl } from '../utils/attribution';
import { trackEvent } from '../utils/analytics';
import {
  trackAddressEntered,
  trackDeliveryCalculated,
  trackDeliveryUnavailable,
  trackDeliveryTooFar,
  trackPaymentInitiated,
  trackClickTelegram,
  trackClickMax,
} from '../utils/metrika';
import AddressBlock from './AddressBlock';
import Calendar from './Calendar';
import { describeItemDates, selectableDates, isoDate, minOrderDate } from '../utils/deliveryDates';
import OrderSummary from './OrderSummary';
import PackagingSelector from './PackagingSelector';
import TimeSlotPicker from './TimeSlotPicker';
import SmsAuthInline from './SmsAuthInline';
import { nowInTZ } from '../utils/timezone';
import { useDeliverySlots, slotStartMinutes, slotCutoffMinutes } from '../deliverySlots';
import { useCatalogLabels, genitivePlural } from '../catalogLabels';


function CheckoutModal({ cart, total, serviceFee, subtotal, stemsSubtotal = 0, stemsServiceFee = 0, bouquetsSubtotalFinal = 0, bouquetsSubtotalBase = 0, bouquetServiceFee = 0, pickupPoints, useBonuses, bonusDiscount, userBonuses = 0, bonusesEarned, oddQuantity, hasPostcard, postcardPrice, onClose, onSuccess, onOutOfStock, onOrderCreated }) {
  const legal = useLegalLinks();
  const slotsConfig = useDeliverySlots();
  const catalogLabels = useCatalogLabels();
  const platform = getPlatform();
  const [webUser, setWebUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  const telegramUser = getTelegramUser();

  // Для веб-платформы: если нет JWT — показываем модалку авторизации
  const isWebGuest = platform === 'web' && !localStorage.getItem('stems_jwt');
  const isWebAuth = platform === 'web' && !!localStorage.getItem('stems_jwt');

  // Загружаем web-юзера при монтировании если уже авторизован
  useEffect(() => {
    if (isWebAuth && !webUser) {
      fetchWebUser().then(u => { if (u) setWebUser(u); }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // При первой загрузке webUser перезаписываем имя/телефон значениями из профиля.
  // Только один раз — потом юзер может править поля.
  const checkoutPrefilledRef = useRef(false);
  useEffect(() => {
    if (!webUser || checkoutPrefilledRef.current) return;
    if (webUser.phone) {
      let d = String(webUser.phone).replace(/\D/g, '');
      if (d.startsWith('8')) d = '7' + d.substring(1);
      if (d.length === 10) d = '7' + d;
      setPhone(d.substring(0, 11));
    }
    const candidate = webUser.tg_first_name || webUser.max_first_name || webUser.first_name || '';
    if (candidate) setName(candidate);
    checkoutPrefilledRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webUser]);

  const [blockedSlotsForDate, setBlockedSlotsForDate] = useState(null);
  const [fullyBlockedDates, setFullyBlockedDates] = useState([]);
  // Даты наличия от сервера: null — не проверены (сеть) → не ограничиваем; [] — общей даты нет.
  const [stockDates, setStockDates] = useState(null);
  const [stockPerItem, setStockPerItem] = useState([]);
  const [useBonusesForDelivery, setUseBonusesForDelivery] = useState(false);

  const STORAGE_KEY = 'stems_checkout_form';

  // Читаем сохранённые данные один раз
  const savedRef = useRef(null);
  if (savedRef.current === null) {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      savedRef.current = raw ? JSON.parse(raw) : {};
    } catch (e) {
      savedRef.current = {};
    }
  }
  const saved = savedRef.current;

  // --- Отдельные useState для каждого поля ---
  // Приоритет для дефолтного имени: TG → Max → user.first_name (заблокированный после первого заказа web-юзера)
  const _initialName = saved.name
    || telegramUser?.tg_first_name
    || telegramUser?.max_first_name
    || telegramUser?.first_name
    || '';
  const [name, setName] = useState(_initialName);
  const [phone, setPhone] = useState(() => {
    const raw = saved.phone || '+7';
    const d = raw.replace(/\D/g, '');
    return d.length >= 1 ? d : '7';
  });
  const [email, setEmail] = useState(saved.email || '');
  // Нет активных точек самовывоза → выбор способа получения не показываем,
  // всегда доставка (черновик с pickup игнорируем — точки могли отключить).
  const hasPickup = pickupPoints.length > 0;
  const [deliveryType, setDeliveryType] = useState(hasPickup ? (saved.delivery_type || 'delivery') : 'delivery');
  const [pickupPointId, setPickupPointId] = useState(saved.pickup_point_id || pickupPoints[0]?.id || 1);
  const [deliveryAddress, setDeliveryAddress] = useState(saved.delivery_address || '');
  const [deliveryApartment, setDeliveryApartment] = useState(saved.delivery_apartment || '');
  const [deliveryEntrance, setDeliveryEntrance] = useState(saved.delivery_entrance || '');
  const [deliveryFloor, setDeliveryFloor] = useState(saved.delivery_floor || '');
  const [deliveryIntercom, setDeliveryIntercom] = useState(saved.delivery_intercom || '');
  const [deliveryApartmentAbsent, setDeliveryApartmentAbsent] = useState(saved.delivery_apartment_absent || false);
  // Дату/время НЕ восстанавливаем из localStorage: наличие могло измениться,
  // клиент должен перевыбрать (особенно после OUT_OF_STOCK / переброса в корзину).
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [isRecipientOther, setIsRecipientOther] = useState(saved.is_recipient_other || false);
  const [recipientName, setRecipientName] = useState(saved.recipient_name || '');
  const [recipientPhone, setRecipientPhone] = useState(() => {
    const raw = saved.recipient_phone || '+7';
    const d = raw.replace(/\D/g, '');
    return d.length >= 1 ? d : '7';
  });
  const [recipientAddress, setRecipientAddress] = useState(saved.recipient_address || '');
  const [recipientApartment, setRecipientApartment] = useState(saved.recipient_apartment || '');
  const [recipientEntrance, setRecipientEntrance] = useState(saved.recipient_entrance || '');
  const [recipientFloor, setRecipientFloor] = useState(saved.recipient_floor || '');
  const [recipientIntercom, setRecipientIntercom] = useState(saved.recipient_intercom || '');
  const [recipientApartmentAbsent, setRecipientApartmentAbsent] = useState(saved.recipient_apartment_absent || false);
  const [packagingType, setPackagingType] = useState(saved.packaging_type || 'transport');
  const [giftMessage, setGiftMessage] = useState(saved.giftMessage || '');
  const [courierComment, setCourierComment] = useState(saved.courier_comment || '');
  // Согласия 152-ФЗ — НЕ сохраняем в localStorage, всегда требуем заново
  const [consentPd, setConsentPd] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);

  // Координаты адресов из Dadata. Восстанавливаем из localStorage —
  // иначе при повторном open чекаута расчёт доставки идёт без координат
  // и бэк отклоняет оплату на 'Для доставки необходимо указать координаты'.
  const [addressCoordinates, setAddressCoordinates] = useState(
    saved.address_coordinates || { delivery: null, recipient: null }
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const calendarRef = useRef(null);
  const timePickerRef = useRef(null);
  const [deliveryCalc, setDeliveryCalc] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const blurCalcDoneRef = useRef(false);
  const resolvedDeliveryPriceRef = useRef(null);
  const [packagingAvailability, setPackagingAvailability] = useState({
    transport: { available: true, quantity: 100 },
    gift: { available: true, quantity: 100 }
  });
  // Полный список упаковок из API (с name/image_url/sort_order). Используется PackagingSelector-ом
  // для отрисовки. До загрузки — пустой, селектор показывает skeleton/legacy.
  const [packagingList, setPackagingList] = useState([]);

  // ── Упаковка: какие варианты предлагать под состав корзины ──────────────
  // У каждой упаковки признаки for_products/for_bouquets. Упаковка одна на заказ:
  // чисто букетная корзина — только упаковки «для букетов»; смешанная —
  // ОБЪЕДИНЕНИЕ (подходит упаковка любого из разделов); чисто стеблевая — «для стеблей».
  // Позиции reserved/qty=0 не уходят в payload — их не учитываем и тут.
  const activeCartItems = cart.filter(it => it.quantity > 0 && !it.reserved_by);
  const cartHasProducts = activeCartItems.some(it => (it.kind || 'product') === 'product');
  const cartHasBouquets = activeCartItems.some(it => it.kind === 'bouquet');
  const productPackagingOptions = packagingList.filter(p => p.for_products !== false);
  const bouquetPackagingOptions = packagingList.filter(p => !!p.for_bouquets);
  let packagingOptions;
  if (cartHasBouquets && !cartHasProducts) {
    packagingOptions = bouquetPackagingOptions;
  } else if (cartHasBouquets && cartHasProducts) {
    packagingOptions = packagingList.filter(p => p.for_products !== false || !!p.for_bouquets);
  } else {
    packagingOptions = productPackagingOptions;
  }
  // Селектора нет только у чисто букетной корзины без упаковок «для букетов» —
  // тогда в заказ уходит packaging_type='none' (подарочные боксы).
  const packagingSelectorVisible = cartHasProducts || bouquetPackagingOptions.length > 0;
  const effectivePackagingType = packagingSelectorVisible ? packagingType : 'none';

  // Если сохранённый/выбранный тип не входит в допустимые для текущей корзины —
  // переключаемся на первый доступный вариант.
  useEffect(() => {
    if (!packagingSelectorVisible || packagingOptions.length === 0) return;
    if (!packagingOptions.some(p => p.type === packagingType)) {
      const first = packagingOptions.find(p => p.quantity > 0) || packagingOptions[0];
      setPackagingType(first.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packagingList, cartHasProducts, cartHasBouquets, packagingType, packagingSelectorVisible]);
  const [fallbackDeliveryPrice, setFallbackDeliveryPrice] = useState(0);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [addressWarning, setAddressWarning] = useState({ delivery: false, recipient: false });
  const [resolvedAddress, setResolvedAddress] = useState(
    saved.resolved_address || { delivery: null, recipient: null }
  );
  const [cityDistrict, setCityDistrict] = useState(
    saved.city_district || { delivery: null, recipient: null }
  );
  const [selectedFromList, setSelectedFromList] = useState(
    saved.selected_from_list || { delivery: false, recipient: false }
  );

  const formRef = useRef(null);

  // Навигацию снизу оставляем видимой — пользователь может вернуться в меню/профиль


  // Если точки самовывоза пропали (пришёл пустой список при открытом чекауте) —
  // принудительно возвращаем доставку: радио скрыто, pickup выбрать нельзя.
  useEffect(() => {
    if (!hasPickup && deliveryType === 'pickup') {
      setDeliveryType('delivery');
    }
  }, [hasPickup, deliveryType]);

  // Дебаунс localStorage — сохраняем через 500мс после последнего изменения
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          name, phone: formatPhone(phone), email, delivery_type: deliveryType,
          pickup_point_id: pickupPointId,
          delivery_address: deliveryAddress, delivery_apartment: deliveryApartment,
          delivery_entrance: deliveryEntrance, delivery_floor: deliveryFloor,
          delivery_intercom: deliveryIntercom, delivery_apartment_absent: deliveryApartmentAbsent,
          // delivery_date/delivery_time намеренно не сохраняем
          is_recipient_other: isRecipientOther,
          recipient_name: recipientName, recipient_phone: formatPhone(recipientPhone),
          recipient_address: recipientAddress, recipient_apartment: recipientApartment,
          recipient_entrance: recipientEntrance, recipient_floor: recipientFloor,
          recipient_intercom: recipientIntercom, recipient_apartment_absent: recipientApartmentAbsent,
          packaging_type: packagingType, giftMessage,
          courier_comment: courierComment,
          // Связанные с адресом данные — нужны для корректного расчёта доставки
          // при повторном открытии чекаута (без них координаты null и бэк отказывает).
          address_coordinates: addressCoordinates,
          resolved_address: resolvedAddress,
          city_district: cityDistrict,
          selected_from_list: selectedFromList,
        }));
      } catch (e) {}
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [name, phone, email, deliveryType, pickupPointId,
      deliveryAddress, deliveryApartment, deliveryEntrance, deliveryFloor,
      deliveryIntercom, deliveryApartmentAbsent,
      isRecipientOther,
      recipientName, recipientPhone,
      recipientAddress, recipientApartment, recipientEntrance, recipientFloor,
      recipientIntercom, recipientApartmentAbsent,
      packagingType, giftMessage, courierComment,
      addressCoordinates, resolvedAddress, cityDistrict, selectedFromList]);

  // Закрытие календаря и time picker по клику мимо
  const datePickerRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Не закрываем если клик по самому toggle (он сам переключит)
      if (datePickerRef.current && datePickerRef.current.contains(e.target)) return;
      if (timePickerRef.current && timePickerRef.current.contains(e.target)) return;
      if (showCalendar && calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
      if (showTimePicker && timePickerRef.current && !timePickerRef.current.contains(e.target)) {
        setShowTimePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showCalendar, showTimePicker]);

  // Загружаем заблокированные даты и наличие упаковок
  useEffect(() => {
    const loadFullyBlockedDates = async () => {
      try {
        const result = await api.getFullyBlockedDates();
        setFullyBlockedDates(result.dates || []);
      } catch (e) {
        console.log('Failed to load fully blocked dates:', e);
      }
    };

    const loadPackagingAvailability = async () => {
      try {
        const packaging = await api.request('/packaging');
        setPackagingList(packaging);
        const availability = {};
        packaging.forEach(p => {
          availability[p.type] = {
            available: p.quantity > 0,
            quantity: p.quantity
          };
        });
        setPackagingAvailability(availability);

        if (!availability[packagingType]?.available) {
          const availableType = packaging.find(p => p.quantity > 0)?.type;
          if (availableType) setPackagingType(availableType);
        }
      } catch (e) {
        console.log('Failed to load packaging availability:', e);
      }
    };

    const loadDeliveryTariffs = async () => {
      try {
        // Новый формат: интервалы любой длины; fallback-цена = цена первой зоны
        const tariffs = await api.request('/settings/delivery-tariff-intervals');
        const first = tariffs?.intervals?.[0];
        if (first && first.price) {
          setFallbackDeliveryPrice(first.price);
        }
      } catch (e) {
        console.log('Failed to load delivery tariffs:', e);
      }
    };

    loadFullyBlockedDates();
    loadPackagingAvailability();
    loadDeliveryTariffs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Загружаем доступные даты на основе наличия товаров в корзине
  useEffect(() => {
    if (!cart || cart.length === 0) return;
    let cancelled = false;  // поздний ответ на прежний состав корзины не должен перебить новый

    const loadAvailableDates = async () => {
      try {
        const activeCart = cart.filter(it => it.quantity > 0 && !it.reserved_by);
        const stems = activeCart.filter(it => (it.kind || 'product') === 'product');
        const bouquets = activeCart.filter(it => it.kind === 'bouquet');
        if (stems.length === 0 && bouquets.length === 0) {
          setStockDates(null);
          setStockPerItem([]);
          return;
        }
        // ОДИН запрос с СУММАРНЫМ спросом: стебли + букеты вместе. Бэк складывает спрос
        // по каждому товару на каждую дату (общий цветок нельзя оверзаказать) → раздельные
        // пересечения «стебли ∩ каждый букет» больше не нужны.
        const items = stems.map(item => ({ product_id: item.id, quantity: item.quantity }));
        const bqs = bouquets.map(b => ({ bouquet_id: b.bouquet_id || b.id, quantity: b.quantity }));
        const r = await api.request('/orders/available-dates', {
          method: 'POST',
          body: JSON.stringify({ items, bouquets: bqs }),
        }).catch(() => null);
        if (cancelled) return;
        // Сеть упала — не ограничиваем (старый список от прежней корзины держать нельзя);
        // наличие на дату всё равно проверит сервер при создании заказа.
        if (!r) { setStockDates(null); setStockPerItem([]); return; }
        // Выбрать можно ТОЛЬКО эти даты (сервер уже ограничил их горизонтом каталога).
        // Раньше здесь запрещались «30 дней вперёд, которых нет в списке» — при пустом
        // списке свободным оказывался 31-й день (30.09 → «31 октября»).
        const dates = (r.dates || []).slice().sort();
        setStockDates(dates);
        setStockPerItem(r.per_item || []);
        // Корзина поменялась и выбранная дата больше не подходит — сбрасываем
        setDeliveryDate(prev => (prev && !dates.includes(prev) ? '' : prev));
      } catch (e) {
        console.log('Failed to load available dates:', e);
      }
    };

    loadAvailableDates();
    return () => { cancelled = true; };
  }, [cart]);

  // Очистка ошибок при изменении валидируемых полей
  useEffect(() => {
    if (error) setError('');
    // error НЕ в deps намеренно — иначе установка ошибки сразу же её сбрасывает
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, email, recipientName, recipientPhone, deliveryAddress, recipientAddress,
      deliveryApartment, recipientApartment, deliveryDate, deliveryTime]);

  // Сброс подтверждения адреса при смене адреса
  useEffect(() => {
    if (addressConfirmed) {
      setAddressConfirmed(false);
      resolvedDeliveryPriceRef.current = null;
    }
    // addressConfirmed НЕ в deps намеренно — иначе цикл (effect меняет addressConfirmed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryAddress, recipientAddress, deliveryType, isRecipientOther]);

  // Автоматический расчет стоимости доставки при изменении адреса
  useEffect(() => {
    const calculateDeliveryCost = async () => {
      if (deliveryType !== 'delivery') {
        setDeliveryCalc(null);
        setDeliveryLoading(false);
        return;
      }

      const address = isRecipientOther ? recipientAddress : deliveryAddress;
      const coords = isRecipientOther ? addressCoordinates.recipient : addressCoordinates.delivery;

      if (!address || address.length < 3) {
        setDeliveryCalc(null);
        return;
      }

      if (!coords) {
        setDeliveryCalc(null);
        return;
      }

      if (blurCalcDoneRef.current) {
        blurCalcDoneRef.current = false;
        return;
      }

      if (!deliveryCalc || deliveryCalc.error) {
        setDeliveryLoading(true);
      }
      try {
        const result = await api.calculateDelivery(address, coords.lat, coords.lon);
        setDeliveryCalc(result);
        if (result && !result.error) {
          trackEvent('address_entered', { address, lat: coords.lat, lon: coords.lon });
          trackAddressEntered(address);
          if (result.requires_negotiation) {
            trackDeliveryUnavailable(address);
          } else {
            trackDeliveryCalculated({ cost: result.price, zone: result.zone, distance: result.distance });
            if (Number(result.price) >= 500) {
              trackDeliveryTooFar(result.price, address);
            }
          }
        }
      } catch (e) {
        console.log('Delivery calculation error:', e);
        setDeliveryCalc({ error: 'Не удалось рассчитать стоимость доставки' });
      } finally {
        setDeliveryLoading(false);
      }
    };

    const timeoutId = setTimeout(calculateDeliveryCost, 1000);
    return () => clearTimeout(timeoutId);
    // deliveryCalc НЕ в deps — читаем предыдущее значение для loading-индикатора,
    // добавление вызвало бы цикл (effect сам меняет deliveryCalc)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryAddress, recipientAddress, deliveryType, isRecipientOther, addressCoordinates]);

  // Загружаем заблокированные слоты при смене даты
  useEffect(() => {
    const loadBlockedSlots = async () => {
      if (!deliveryDate) {
        setBlockedSlotsForDate(null);
        return;
      }
      try {
        const result = await api.getBlockedSlotsForDate(deliveryDate);
        setBlockedSlotsForDate(result.blocked);
      } catch (e) {
        console.log('Failed to load blocked slots:', e);
        setBlockedSlotsForDate(null);
      }
    };
    loadBlockedSlots();
  }, [deliveryDate]);

  // Сбрасываем время если выбранный слот стал заблокирован
  useEffect(() => {
    if (deliveryTime && blockedSlotsForDate) {
      if (blockedSlotsForDate === 'all' ||
          (Array.isArray(blockedSlotsForDate) && blockedSlotsForDate.includes(deliveryTime))) {
        setDeliveryTime('');
      }
    }
    // Срабатываем только на изменение списка блокировок, не при изменении deliveryTime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedSlotsForDate]);

  // Для не-веб платформ: если нет юзера — не рендерим.
  // Early return ПОСЛЕ всех хуков — требование Rules of Hooks.
  if (!telegramUser && platform !== 'web') return null;
  // Даты, отсечка и слоты — в поясе магазина из настроек: пока они не пришли, считать
  // нечем (подстановок нет). Обычно к клику по «Оформить» они уже есть; если нет —
  // показываем оформление с загрузкой (useDeliverySlots повторяет запрос сам).
  if (!slotsConfig.loaded) {
    return (
      <div className="checkout-page">
        <div className="checkout-header">
          <button className="checkout-back" onClick={onClose} type="button" aria-label="Назад">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h2>Корзина</h2>
        </div>
        <SectionLoader />
      </div>
    );
  }

  // Минимальная дата: если отсечка последнего слота уже прошла (порог ≥0 —
  // от начала слота, <0 — приём в идущий слот до |N| минут до конца) —
  // сегодня недоступно
  const getMinDate = () => minOrderDate(
    nowInTZ(slotsConfig.shopTimezone), slotsConfig.timeSlots, slotsConfig.leadTimeMinutes);

  // Какие даты реально выбрать: даты сервера, начиная с минимальной (после отсечки
  // последнего слота «сегодня» уже не принять). [] — дату выбрать нельзя: объясняем по
  // позициям и не даём оформить. null — сервер не ответил, не ограничиваем.
  const minDateIso = isoDate(getMinDate());
  const pickableDates = selectableDates(stockDates, minDateIso);
  const noCommonDate = Array.isArray(pickableDates) && pickableDates.length === 0;
  const noDateRows = noCommonDate ? describeItemDates(stockPerItem, {
    product: Object.fromEntries((cart || []).filter(it => (it.kind || 'product') === 'product').map(it => [it.id || it.product_id, it.name])),
    bouquet: Object.fromEntries((cart || []).filter(it => it.kind === 'bouquet').map(it => [it.bouquet_id || it.id, it.name])),
  }, minDateIso) : [];
  // Тексты «нет даты»: несколько позиций — заголовок, список и «разными заказами»;
  // одна — только её строка («Георгин — заказы на сегодня уже не принимаются») и
  // «Измените состав заказа.» (другой даты у неё нет — иначе сервер бы её вернул).
  const noDateMulti = noDateRows.length > 1;
  const noDateTitle = noDateMulti ? 'Нет даты, на которую можно собрать весь заказ' : null;
  const noDateAction = noDateMulti
    ? 'Вернитесь в корзину и оформите эти позиции разными заказами.'
    : 'Измените состав заказа.';

  // Форматирование телефона для отображения: +7 (XXX) XXX-XX-XX
  const formatPhone = (digits) => {
    if (!digits || digits.length === 0) return '+7';
    let d = digits.replace(/\D/g, '');
    if (d.startsWith('8')) d = '7' + d.slice(1);
    if (d.length > 0 && !d.startsWith('7')) d = '7' + d;

    let r = '+7';
    if (d.length > 1) r += ' (' + d.slice(1, 4);
    if (d.length >= 4) r += ') ';
    if (d.length > 4) r += d.slice(4, 7);
    if (d.length > 7) r += '-' + d.slice(7, 9);
    if (d.length > 9) r += '-' + d.slice(9, 11);
    return r;
  };

  // Телефон: храним только цифры, форматируем при отображении
  const parsePhoneDigits = (value, prev) => {
    let d = value.replace(/\D/g, '');
    if (d.startsWith('8')) d = '7' + d.slice(1);
    if (d.length > 0 && !d.startsWith('7')) d = '7' + d;
    d = d.slice(0, 11);
    // Backspace через скобку: цифры не изменились, но текст стал короче
    if (prev && d === prev && value.length < formatPhone(prev).length) {
      d = d.slice(0, -1);
    }
    return d;
  };

  const handlePhoneChange = (value) => {
    setPhone(parsePhoneDigits(value, phone));
  };

  const handleRecipientPhoneChange = (value) => {
    setRecipientPhone(parsePhoneDigits(value, recipientPhone));
  };

  const allTimeSlots = slotsConfig.timeSlots.map(value => ({
    value,
    label: value.replace('-', ' - '),
    startMinutes: slotStartMinutes(value),
  }));

  const getAvailableTimeSlots = () => {
    const now = nowInTZ(slotsConfig.shopTimezone);
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    let availableSlots = allTimeSlots;

    if (blockedSlotsForDate === 'all') {
      return [];
    }
    if (Array.isArray(blockedSlotsForDate) && blockedSlotsForDate.length > 0) {
      availableSlots = availableSlots.filter(
        slot => !blockedSlotsForDate.includes(slot.value)
      );
    }

    if (deliveryDate === today) {
      availableSlots = availableSlots.filter(
        slot => slotCutoffMinutes(slot.value, slotsConfig.leadTimeMinutes) >= nowMinutes
      );
    }

    return availableSlots;
  };

  const timeSlots = getAvailableTimeSlots();

  const getDeliveryPrice = () => {
    if (deliveryType === 'pickup') {
      const point = pickupPoints.find(p => p.id === pickupPointId);
      return point?.price || 0;
    }
    if (deliveryType === 'delivery') {
      if (deliveryCalc && !deliveryCalc.error && !deliveryCalc.requires_negotiation && deliveryCalc.price) {
        return deliveryCalc.price;
      }
      const addr = isRecipientOther ? recipientAddress : deliveryAddress;
      const coords = isRecipientOther ? addressCoordinates.recipient : addressCoordinates.delivery;
      if (addr && addr.length >= 5 && !coords) {
        return fallbackDeliveryPrice;
      }
      return 0;
    }
    return 0;
  };

  const deliveryPrice = getDeliveryPrice();
  const totalWithDelivery = total + deliveryPrice;

  // Автоматическая проверка адреса при потере фокуса
  const handleAddressBlur = async (type, currentValue) => {
    const addr = currentValue || (type === 'recipient' ? recipientAddress : deliveryAddress);
    const coords = type === 'recipient' ? addressCoordinates.recipient : addressCoordinates.delivery;

    if (coords) {
      if (selectedFromList[type]) {
        setAddressWarning(prev => ({ ...prev, [type]: false }));
      }
      return;
    }

    if (!addr || addr.length < 3) {
      setAddressWarning(prev => ({ ...prev, [type]: false }));
      return;
    }

    try {
      setDeliveryLoading(true);
      const result = await api.request(`/address/suggest?query=${encodeURIComponent(addr)}&count=1`);
      const suggestion = result.suggestions?.[0];

      if (suggestion?.data?.house && suggestion?.data?.geo_lat && suggestion?.data?.geo_lon) {
        const newCoords = { lat: suggestion.data.geo_lat, lon: suggestion.data.geo_lon };
        const dadataAddr = suggestion.value;
        setAddressCoordinates(prev => ({ ...prev, [type]: newCoords }));
        setResolvedAddress(prev => ({ ...prev, [type]: dadataAddr }));
        setCityDistrict(prev => ({ ...prev, [type]: suggestion.data.city_district || null }));

        if (!suggestion.data.postal_code) {
          setAddressWarning(prev => ({ ...prev, [type]: true }));
        } else {
          const inputWords = addr.trim().toLowerCase().split(/[\s,]+/).filter(w => w.length > 0);
          const dadataWords = dadataAddr.toLowerCase().split(/[\s,]+/).filter(w => w.length > 0);
          const allWordsFound = inputWords.every(word =>
            dadataWords.some(dw => dw === word || (dw.endsWith(word) && word.length >= 3))
          );
          setAddressWarning(prev => ({ ...prev, [type]: !allWordsFound }));
        }

        try {
          const calc = await api.calculateDelivery(dadataAddr, newCoords.lat, newCoords.lon);
          setDeliveryCalc(calc);
          blurCalcDoneRef.current = true;
        } catch (e) {
          console.log('Delivery calc after blur resolve:', e);
        }
      } else if (suggestion && !suggestion.data?.house) {
        setResolvedAddress(prev => ({ ...prev, [type]: null }));
        setAddressCoordinates(prev => ({ ...prev, [type]: null }));
        setAddressWarning(prev => ({ ...prev, [type]: true }));
      } else {
        setResolvedAddress(prev => ({ ...prev, [type]: null }));
        setAddressCoordinates(prev => ({ ...prev, [type]: null }));
        setAddressWarning(prev => ({ ...prev, [type]: true }));
      }
    } catch (e) {
      console.log('Address blur resolve error:', e);
      setResolvedAddress(prev => ({ ...prev, [type]: null }));
      setAddressCoordinates(prev => ({ ...prev, [type]: null }));
      setAddressWarning(prev => ({ ...prev, [type]: true }));
    } finally {
      setDeliveryLoading(false);
    }
  };

  const highlightField = (fieldId) => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      const input = el.querySelector('input, select, .checkout-date-picker');
      if (input) {
        input.style.transition = 'background 0.3s';
        input.style.background = '#FFE0E0';
        if (input.tagName === 'INPUT' || input.tagName === 'SELECT') input.focus();
        setTimeout(() => {
          input.style.background = '';
          setTimeout(() => { input.style.transition = ''; }, 300);
        }, 1000);
      }
    }, 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!addressConfirmed) {
      resolvedDeliveryPriceRef.current = null;
    }

    // Защита от пустого заказа: после фильтра qty>0 && !reserved_by в корзине
    // не должно остаться 0 позиций (иначе уходил пустой заказ только за доставку, #3695).
    const activeItems = cart.filter(it => it.quantity > 0 && !it.reserved_by);
    if (activeItems.length === 0) {
      setError('Корзина пуста или товары стали недоступны. Добавьте товар, чтобы оформить заказ.');
      return;
    }

    // Проверка телефона (11 цифр: 7 + 10)
    if (!phone || phone.length < 11) {
      highlightField('field-phone');
      return;
    }

    // Проверка email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      highlightField('field-email');
      return;
    }

    if (deliveryType === 'delivery' && deliveryLoading) {
      return;
    }

    if (deliveryType === 'delivery') {
      const addrKey = isRecipientOther ? 'recipient' : 'delivery';
      const coords = addressCoordinates[addrKey];
      if (!coords) {
        highlightField(isRecipientOther ? 'field-recipient-address' : 'field-address');
        return;
      }
    }

    if (deliveryType === 'delivery' && !isRecipientOther && !deliveryAddress) {
      highlightField('field-address');
      return;
    }

    if (deliveryType === 'delivery' && !isRecipientOther && !deliveryApartmentAbsent && !deliveryApartment) {
      highlightField('field-apartment');
      return;
    }

    if (isRecipientOther) {
      if (!recipientName) {
        highlightField('field-recipient-name');
        return;
      }

      if (!recipientPhone || recipientPhone.length < 11) {
        highlightField('field-recipient-phone');
        return;
      }

      if (deliveryType === 'delivery' && !recipientAddress) {
        highlightField('field-recipient-address');
        return;
      }

      if (deliveryType === 'delivery' && !recipientApartmentAbsent && !recipientApartment) {
        highlightField('field-recipient-apartment');
        return;
      }
    }

    if (packagingSelectorVisible && !packagingAvailability[packagingType]?.available) {
      setError('Выбранная упаковка недоступна');
      return;
    }

    if (noCommonDate) {
      setError(noDateMulti
        ? 'Нет даты, на которую можно собрать весь заказ — оформите позиции разными заказами'
        : `${noDateRows[0] ? `${noDateRows[0].name} — ${noDateRows[0].datesStr}. ` : ''}Измените состав заказа.`);
      highlightField('field-date');
      return;
    }

    if (!deliveryDate) {
      highlightField('field-date');
      return;
    }

    if (!deliveryTime) {
      highlightField('field-time');
      return;
    }

    // Авторезолв адреса при сабмите (если клиент ввёл вручную, не из списка)
    if (deliveryType === 'delivery' && !addressConfirmed) {
      const addressKey = isRecipientOther ? 'recipient' : 'delivery';
      const wasSelectedFromList = selectedFromList[addressKey];

      if (!wasSelectedFromList && !addressCoordinates[addressKey]) {
        const addr = isRecipientOther ? recipientAddress : deliveryAddress;
        if (addr && addr.length >= 3) {
          try {
            setIsLoading(true);
            const result = await api.request(`/address/suggest?query=${encodeURIComponent(addr)}&count=1`);
            const suggestion = result.suggestions?.[0];

            if (suggestion?.data?.house && suggestion?.data?.geo_lat && suggestion?.data?.geo_lon) {
              const newCoords = { lat: suggestion.data.geo_lat, lon: suggestion.data.geo_lon };
              const dadataAddr = suggestion.value;
              setAddressCoordinates(prev => ({ ...prev, [addressKey]: newCoords }));
              setResolvedAddress(prev => ({ ...prev, [addressKey]: dadataAddr }));

              try {
                const calc = await api.calculateDelivery(dadataAddr, newCoords.lat, newCoords.lon);
                setDeliveryCalc(calc);
              } catch (e) {
                console.log('Delivery calc after auto-resolve:', e);
              }
              setIsLoading(false);
            } else {
              setIsLoading(false);
              highlightField(isRecipientOther ? 'field-recipient-address' : 'field-address');
              return;
            }
          } catch (e) {
            console.log('Auto-resolve address error:', e);
            setIsLoading(false);
            highlightField(isRecipientOther ? 'field-recipient-address' : 'field-address');
            return;
          }
        }
      }
    }

    setIsLoading(true);

    try {
      let orderDeliveryAddress = '';
      if (deliveryType === 'delivery') {
        orderDeliveryAddress = isRecipientOther ? recipientAddress : deliveryAddress;
      }

      let apartment = null, entrance = null, floor = null, intercom = null;
      if (deliveryType === 'delivery') {
        const isAbsent = isRecipientOther ? recipientApartmentAbsent : deliveryApartmentAbsent;
        apartment = isAbsent ? null : (isRecipientOther ? recipientApartment : deliveryApartment);
        entrance = isAbsent ? null : (isRecipientOther ? recipientEntrance : deliveryEntrance) || null;
        floor = isAbsent ? null : (isRecipientOther ? recipientFloor : deliveryFloor) || null;
        intercom = isAbsent ? null : (isRecipientOther ? recipientIntercom : deliveryIntercom) || null;
      }

      const addressKey = isRecipientOther ? 'recipient' : 'delivery';
      const dadataAddr = deliveryType === 'pickup' ? null : (resolvedAddress[addressKey] || null);
      const district = deliveryType === 'pickup' ? null : (cityDistrict[addressKey] || null);
      const isAddressVerified = !orderDeliveryAddress || selectedFromList[addressKey];

      const effectiveUser = webUser || telegramUser || {};
      // Гостевой путь удалён: в вебе кнопка «Оформить» заблокирована до входа по SMS
      // (isWebGuest), поэтому сюда попадают только авторизованные.
      const sourcePlatform = effectiveUser.platform || 'telegram';

      const orderData = {
        telegram_id: effectiveUser.telegram_id || undefined,
        max_id: effectiveUser.max_id || undefined,
        source_platform: sourcePlatform,
        device_platform: getDevicePlatform(),
        yclid: getStoredYclid(),  // рекламная метка Я.Директа (веб-путь), если есть
        touch_token: getTouchToken(),     // откуда пришёл (модуль «Источники заказов»)
        web_client_id: getWebClientId(),
        first_name: name,
        last_name: effectiveUser.last_name,
        username: effectiveUser.username,
        phone: formatPhone(phone),
        email: email,
        delivery_type: deliveryType,
        pickup_point_id: deliveryType === 'pickup' ? pickupPointId : null,
        delivery_address: orderDeliveryAddress,
        delivery_address_dadata: dadataAddr,
        city_district: district,
        address_verified: isAddressVerified,
        apartment: (isRecipientOther ? recipientApartmentAbsent : deliveryApartmentAbsent) ? null : apartment,
        no_apartment: deliveryType === 'delivery' && (isRecipientOther ? recipientApartmentAbsent : deliveryApartmentAbsent),
        entrance: entrance,
        floor: floor,
        intercom: intercom,
        delivery_date: deliveryDate,
        delivery_time: deliveryTime,
        delivery_lat: deliveryType === 'delivery' ? (addressCoordinates[addressKey]?.lat || null) : null,
        delivery_lon: deliveryType === 'delivery' ? (addressCoordinates[addressKey]?.lon || null) : null,
        is_recipient_other: isRecipientOther,
        recipient_name: recipientName,
        recipient_phone: formatPhone(recipientPhone),
        // 'none' только у чисто букетной корзины без упаковок «для букетов»
        // (подарочные боксы); во всех остальных случаях — выбранный тип.
        packaging_type: effectivePackagingType,
        greeting: hasPostcard ? giftMessage : '',
        postcard_price: hasPostcard ? postcardPrice : 0,
        courier_comment: deliveryType === 'delivery' ? courierComment.trim() : '',
        items: cart
          .filter(item => item.quantity > 0 && !item.reserved_by)
          .map(item => {
            const isBouquet = item.kind === 'bouquet';
            return isBouquet
              ? { bouquet_id: item.bouquet_id || item.id, quantity: item.quantity }
              : { product_id: item.id, quantity: item.quantity };
          }),
        use_bonuses: useBonuses,
        use_bonuses_for_delivery: !!useBonusesForDelivery,
        odd_quantity: oddQuantity || false,
        consent_pd: consentPd,
        consent_marketing: consentMarketing,
        // Нативный Telegram-инвойс: бэкенд не создаёт redirect-платёж заранее.
        // Если инвойс не взлетит (провайдер не подключён) — fallback ниже
        // создаст redirect-платёж через /payment/order-url.
        payment_channel: canOpenInvoice() ? 'tg_invoice' : 'redirect',
      };

      try {
        const order = await api.createOrder(orderData);
        trackEvent('order_created', { order_id: order.id });

        try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
        if (onOrderCreated) onOrderCreated();

        if (order.payment_status === 'paid') {
          trackEvent('order_paid', { order_id: order.id });
          console.log('Order paid by bonuses, no payment needed');
          setIsLoading(false);
          onSuccess(order);
          return;
        }

        // Нативный Telegram-инвойс: оплата внутри Telegram, из мини-аппа
        // никто не уходит (payments.js). Статус придёт в invoiceClosed →
        // App.jsx покажет попап и «Мои заказы». false → redirect-флоу ниже.
        if (await payViaTgInvoice({ id: order.id, kind: 'order', orderNumber: order.order_number, total: order.total })) {
          onSuccess(order);
          trackPaymentInitiated(order.id, order.total);
          setIsLoading(false);
          return;
        }

        // Порядок важен — на iOS вэбвью при переходе в Safari JS может
        // приостановиться, и код ПОСЛЕ openLink не выполнится. Поэтому всё
        // что должно отработать гарантированно — делаем ДО openLink:
        // 1) localStorage маркер — для visibilitychange-handler'а в App.jsx,
        //    чтобы при возврате из Safari показать попап со статусом оплаты.
        // 2) onSuccess(order) → handleOrderSuccess: clearCart + закрытие
        //    модалки + переход в «Мои заказы». Если этот блок отработает
        //    ПОСЛЕ openLink и JS приостановится — корзина и форма чекаута
        //    остаются заполненными, кнопка «Оформить» доступна, клиент при
        //    возврате создаёт дубль (инцидент #2715/#2716, 2026-05-07).
        // 3) platformOpenLink — после (TG Desktop Mac любит свежий
        //    user-gesture, но setState'ы выше синхронны и его не ломают).
        // 4) trackPaymentInitiated — последним, аналитика не критична.
        try {
          localStorage.setItem('stems_pending_payment', order.id.toString());
          localStorage.removeItem('stems_pending_payment_cert');
        } catch (e) {}

        onSuccess(order);

        if (order.payment_url) {
          platformOpenLink(order.payment_url);
        }

        trackPaymentInitiated(order.id, order.total);

        if (!order.payment_url) {
          // Редкий случай — ЮКасса упала при создании. Заказ всё равно
          // сохранён в pending; юзер увидит его в «Мои заказы» с кнопкой
          // «Оплатить», которая попытается создать платёж заново через
          // /api/payment/order-url/{id}.
          try {
            const _r = await api.getOrderPaymentUrl(order.id);
            if (_r?.payment_url) {
              platformOpenLink(_r.payment_url);
            } else {
              setError('Не удалось создать платёж. Заказ сохранён — оплатите из «Мои заказы».');
            }
          } catch (_e) {
            setError('Не удалось создать платёж. Заказ сохранён — оплатите из «Мои заказы».');
          }
        }
        setIsLoading(false);
      } catch (e) {
        console.log('Order creation error:', e);
        const msg = e.message || '';
        if (msg.startsWith('OUT_OF_STOCK|') && onOutOfStock) {
          const parts = msg.split('|').slice(1);
          const items = parts.map(p => {
            const lastColon = p.lastIndexOf(':');
            const available = parseInt(p.substring(lastColon + 1), 10);
            const rest = p.substring(0, lastColon);
            const firstColon = rest.indexOf(':');
            return {
              productId: parseInt(rest.substring(0, firstColon), 10),
              productName: rest.substring(firstColon + 1),
              available: isNaN(available) ? 0 : available
            };
          });
          onOutOfStock(items);
          // Закрываем чекаут — клиент возвращается в корзину, видит тост,
          // qty уменьшено. Введённые поля сохраняются в localStorage и
          // подставятся при следующем open (кроме delivery_date/time).
          if (typeof onClose === 'function') {
            onClose();
          }
        } else {
          const isNetworkOrUnknown = !msg || msg === 'Request failed' || /failed to fetch|networkerror|load failed/i.test(msg);
          if (isNetworkOrUnknown) {
            setError('Не удалось связаться с платёжным провайдером. Попробуйте позже или свяжитесь с поддержкой сервиса.');
          } else {
            setError(msg);
          }
        }
        setIsLoading(false);
        return;
      }

    } catch (error) {
      console.error('Order error:', error);
      const m = error?.message || '';
      const isNetworkOrUnknown = !m || m === 'Request failed' || /failed to fetch|networkerror|load failed/i.test(m);
      setError(isNetworkOrUnknown
        ? 'Не удалось связаться с платёжным провайдером. Попробуйте позже или свяжитесь с поддержкой сервиса.'
        : m);
      setIsLoading(false);
    }
  };

  const handleAddressChange = (type, address, data) => {
    if (type === 'recipient') {
      setRecipientAddress(address);
    } else {
      setDeliveryAddress(address);
    }
    if (data && data.geo_lat && data.geo_lon) {
      setAddressCoordinates(prev => ({ ...prev, [type]: { lat: data.geo_lat, lon: data.geo_lon } }));
      setResolvedAddress(prev => ({ ...prev, [type]: address }));
      setCityDistrict(prev => ({ ...prev, [type]: data.city_district || null }));
      setAddressWarning(prev => ({ ...prev, [type]: false }));
      setSelectedFromList(prev => ({ ...prev, [type]: true }));
    } else if (data !== undefined) {
      setAddressCoordinates(prev => ({ ...prev, [type]: null }));
      setResolvedAddress(prev => ({ ...prev, [type]: null }));
      setCityDistrict(prev => ({ ...prev, [type]: null }));
      setAddressWarning(prev => ({ ...prev, [type]: false }));
      setSelectedFromList(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleAddressFieldChange = (type, field, value) => {
    const setters = type === 'recipient' ? {
      apartment: setRecipientApartment,
      entrance: setRecipientEntrance,
      floor: setRecipientFloor,
      intercom: setRecipientIntercom,
      apartment_absent: setRecipientApartmentAbsent,
    } : {
      apartment: setDeliveryApartment,
      entrance: setDeliveryEntrance,
      floor: setDeliveryFloor,
      intercom: setDeliveryIntercom,
      apartment_absent: setDeliveryApartmentAbsent,
    };
    if (setters[field]) setters[field](value);
    if (field === 'apartment_absent' && value) {
      const aptSetter = isRecipientOther ? setRecipientApartment : setDeliveryApartment;
      aptSetter('');
    }
  };

  const formatSelectedDate = (dateStr) => {
    if (!dateStr) return 'Выберите дату';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' });
  };

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <button className="checkout-back" onClick={onClose} type="button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2>Корзина</h2>
      </div>

        {/* Баннер авторизации для веб-гостей */}
        {isWebGuest && (
          <div style={{
            // Фирменная мягкая подложка вместо чужого зелёного (как в профиле)
            background: 'var(--primary-soft-bg)',
            borderRadius: '10px',
            padding: '14px 16px',
            margin: '12px 20px 0',
            fontSize: '14px',
            color: 'var(--text-color)',
            textAlign: 'center',
          }}>
            <div style={{ marginBottom: '10px' }}>
              После авторизации вы сможете отслеживать статус выполнения и историю заказов, пополнять и списывать счёт {BRAND.name}.
            </div>

            {authOpen ? (
              <div style={{ marginBottom: '12px', maxWidth: '300px', margin: '0 auto 12px' }}>
                <SmsAuthInline
                  onSuccess={async () => {
                    const fresh = await fetchWebUser();
                    if (fresh) {
                      setWebUser(fresh);
                      if (fresh.phone) {
                        let d = String(fresh.phone).replace(/\D/g, '');
                        if (d.startsWith('8')) d = '7' + d.substring(1);
                        if (d.length === 10) d = '7' + d;
                        setPhone(d.substring(0, 11));
                      }
                    }
                    setAuthOpen(false);
                  }}
                  onCancel={() => setAuthOpen(false)}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAuthOpen(true)}
                style={{
                  background: 'var(--primary-color)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  color: 'var(--on-primary)',
                  marginBottom: '10px',
                }}
              >
                Авторизация через SMS
              </button>
            )}

            <div style={{ fontSize: '12px', color: '#555', margin: '4px 0 8px' }}>
              или оформите заказ в мини-приложении
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <a
                href={tgBotUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClickTelegram('checkout_modal')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: '#2AABEE', color: 'white', textDecoration: 'none',
                  borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '600',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.492-1.302.48-.428-.012-1.252-.242-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                Telegram
              </a>
              <a
                href={maxBotUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClickMax('checkout_modal')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: 'linear-gradient(135deg, #00C8FF 0%, #5B6FFF 50%, #A855F7 100%)',
                  color: 'white', textDecoration: 'none',
                  borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '600',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12c0 1.77.46 3.43 1.27 4.88L2 22l5.12-1.24A9.96 9.96 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 14.5a4.5 4.5 0 110-9 4.5 4.5 0 010 9z" fill="white"/></svg>
                Max
              </a>
            </div>
          </div>
        )}

        <form className="checkout-form" ref={formRef} onSubmit={handleSubmit} noValidate>
          <div className="checkout-field">
            <label>Имя заказчика</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ваше имя"
            />
          </div>

          <div className="checkout-field" id="field-phone">
            <label>Телефон *</label>
            <input
              type="tel"
              value={formatPhone(phone)}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="+7 (999) 888-22-22"
              maxLength={18}
            />
          </div>

          <div className="checkout-field" id="field-email">
            <label>E-mail *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@mail.ru"
            />
          </div>

          <div className="checkout-checkbox">
            <label>
              <input
                type="checkbox"
                checked={isRecipientOther}
                onChange={(e) => setIsRecipientOther(e.target.checked)}
              />
              <span>Получатель другой человек</span>
            </label>
          </div>

          {/* Тип доставки */}
          {/* Выбор способа получения — только если есть активные точки самовывоза */}
          {hasPickup && (
            <div className="checkout-field">
              <label>Способ получения</label>
              <div className="delivery-type-options">
                <label className={`delivery-type-option ${deliveryType === 'delivery' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="delivery_type"
                    value="delivery"
                    checked={deliveryType === 'delivery'}
                    onChange={(e) => setDeliveryType(e.target.value)}
                  />
                  <span><StatusIcon name="truck" size={15} />Доставка</span>
                </label>
                <label className={`delivery-type-option ${deliveryType === 'pickup' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="delivery_type"
                    value="pickup"
                    checked={deliveryType === 'pickup'}
                    onChange={(e) => setDeliveryType(e.target.value)}
                  />
                  <span><StatusIcon name="pin" size={15} />Самовывоз</span>
                </label>
              </div>
            </div>
          )}

          {/* Самовывоз */}
          {deliveryType === 'pickup' && (
            <div className="checkout-field">
              {pickupPoints.length > 1 ? (
                <select
                  className="checkout-input"
                  value={pickupPointId}
                  onChange={(e) => setPickupPointId(Number(e.target.value))}
                >
                  {pickupPoints.map(p => (
                    <option key={p.id} value={p.id}>{p.address || p.name}</option>
                  ))}
                </select>
              ) : (
                <div className="checkout-address">
                  <HeroIcon name="pin" size={16} color="currentColor" inline /> {pickupPoints.find(p => p.id === pickupPointId)?.address || 'адрес уточните у магазина'}
                </div>
              )}
            </div>
          )}

          {/* Доставка - адрес */}
          {deliveryType === 'delivery' && !isRecipientOther && (
            <AddressBlock
              address={deliveryAddress}
              apartment={deliveryApartment}
              entrance={deliveryEntrance}
              floor={deliveryFloor}
              intercom={deliveryIntercom}
              apartmentAbsent={deliveryApartmentAbsent}
              addressWarning={addressWarning.delivery}
              deliveryLoading={deliveryLoading}
              deliveryCalc={deliveryCalc}
              onAddressChange={(address, data) => handleAddressChange('delivery', address, data)}
              onAddressBlur={(val) => handleAddressBlur('delivery', val)}
              onFieldChange={(field, value) => handleAddressFieldChange('delivery', field, value)}
              fieldIdPrefix="field-"
              placeholder="Улица, дом "
            />
          )}

          {isRecipientOther && (
            <>
              <div className="checkout-field" id="field-recipient-name">
                <label>Имя получателя *</label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Введите имя получателя"
                />
              </div>

              <div className="checkout-field" id="field-recipient-phone">
                <label>Телефон получателя *</label>
                <input
                  type="tel"
                  value={formatPhone(recipientPhone)}
                  onChange={(e) => handleRecipientPhoneChange(e.target.value)}
                  placeholder="+7 (999) 888-22-22"
                  maxLength={18}
                />
              </div>

              {deliveryType === 'delivery' && (
                <AddressBlock
                  address={recipientAddress}
                  apartment={recipientApartment}
                  entrance={recipientEntrance}
                  floor={recipientFloor}
                  intercom={recipientIntercom}
                  apartmentAbsent={recipientApartmentAbsent}
                  addressWarning={addressWarning.recipient}
                  deliveryLoading={deliveryLoading}
                  deliveryCalc={deliveryCalc}
                  onAddressChange={(address, data) => handleAddressChange('recipient', address, data)}
                  onAddressBlur={(val) => handleAddressBlur('recipient', val)}
                  onFieldChange={(field, value) => handleAddressFieldChange('recipient', field, value)}
                  fieldIdPrefix="field-recipient-"
                  placeholder="Улица, дом получателя "
                />
              )}
            </>
          )}

          {/* Комментарий для курьера — только при доставке */}
          {deliveryType === 'delivery' && (
            <div className="checkout-field">
              <label style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                Комментарий для курьера
              </label>
              <textarea
                value={courierComment}
                onChange={(e) => {
                  const text = e.target.value.slice(0, 100);
                  setCourierComment(text);
                }}
                onFocus={(e) => {
                  setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                }}
                maxLength={100}
                placeholder="Например: домофон не работает, позвоните за 30 минут"
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  resize: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  // Разрешаем выделение текста и iOS long-press menu (copy/paste)
                  WebkitUserSelect: 'text', userSelect: 'text',
                  WebkitTouchCallout: 'default',
                  touchAction: 'auto',
                }}
              />
              <div style={{
                textAlign: 'right',
                fontSize: '12px',
                color: courierComment.length >= 85 ? '#f44336' : '#999',
                marginTop: '4px'
              }}>
                {courierComment.length}/100
              </div>
            </div>
          )}

          {/* Дата доставки - по клику */}
          <div className="checkout-field" id="field-date">
            <label>Дата доставки/самовывоза</label>
            {noCommonDate ? (
              <div className="checkout-no-common-date" style={{
                background: '#FFF3CD', border: '1px solid #FFCA28', borderRadius: '12px',
                padding: '12px 14px', fontSize: '13px', color: '#5D4037', lineHeight: 1.5,
              }}>
                {noDateTitle && <div style={{ fontWeight: 600, marginBottom: 6 }}>{noDateTitle}</div>}
                {noDateMulti ? (
                  <ul style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>
                    {noDateRows.map((row, i) => (
                      <li key={i}><span style={{ fontWeight: 500 }}>{row.name}</span> — {row.datesStr}</li>
                    ))}
                  </ul>
                ) : noDateRows[0] && (
                  <div style={{ marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{noDateRows[0].name}</span> — {noDateRows[0].datesStr}.
                  </div>
                )}
                <div>{noDateAction}</div>
              </div>
            ) : (
            <>
            <div
              className="checkout-date-picker"
              ref={datePickerRef}
              onClick={() => setShowCalendar(!showCalendar)}
            >
              <span>{formatSelectedDate(deliveryDate)}</span>
              <span className="checkout-date-icon"><FieldIcon name="calendar" /></span>
            </div>
            {showCalendar && (
              <div ref={calendarRef}>
                <Calendar
                  selectedDate={deliveryDate}
                  onSelectDate={(date) => {
                    setDeliveryDate(date);
                    setDeliveryTime('');
                    setShowTimePicker(false);
                    setShowCalendar(false);
                  }}
                  minDate={getMinDate()}
                  onClose={() => setShowCalendar(false)}
                  disabledDates={fullyBlockedDates}
                  availableDates={pickableDates || []}
                  allowedDates={pickableDates}
                />
              </div>
            )}
            </>
            )}
          </div>

          <div ref={timePickerRef}>
          <TimeSlotPicker
            selectedTime={deliveryTime}
            timeSlots={timeSlots}
            hasDate={!!deliveryDate}
            isOpen={showTimePicker}
            onToggle={() => setShowTimePicker(!showTimePicker)}
            onSelect={(value) => { setDeliveryTime(value); setShowTimePicker(false); }}
          />
          </div>

          {/* Упаковка: варианты отфильтрованы по признакам for_products/for_bouquets
              (см. расчёт packagingOptions выше). Селектора нет только у чисто
              букетной корзины без упаковок «для букетов» — тогда надпись. */}
          {packagingSelectorVisible ? (
            <div>
              {cartHasBouquets && cartHasProducts && bouquetPackagingOptions.length === 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 12, color: '#6B6B68',
                  margin: '0 0 8px 4px',
                }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 14, height: 14, borderRadius: '50%',
                    background: '#1F3D36', color: '#fff', fontSize: 9, fontWeight: 600, flexShrink: 0,
                  }}>i</span>
                  Упаковка только для {genitivePlural(catalogLabels.products) || `раздела «${catalogLabels.products}»`}.
                  {' '}{catalogLabels.bouquets} доставляются в подарочных боксах.
                </div>
              )}
              <PackagingSelector
                packagingList={packagingOptions}
                selectedType={packagingType}
                packagingAvailability={packagingAvailability}
                onSelect={(type) => setPackagingType(type)}
              />
            </div>
          ) : (
            <div style={{
              padding: '14px 16px', background: '#F4F8F6',
              borderRadius: 10, fontSize: 13, color: '#1F3D36',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 20 }}>💐</span>
              {catalogLabels.bouquets} доставляются в подарочных боксах
            </div>
          )}

          {/* Текст открытки (только если выбрана открытка в корзине) */}
          {hasPostcard && (
            <div className="checkout-field">
              <label style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                Текст открытки
              </label>
              <textarea
                value={giftMessage}
                onChange={(e) => {
                  const text = e.target.value.slice(0, 140);
                  setGiftMessage(text);
                }}
                onFocus={(e) => {
                  setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                }}
                maxLength={140}
                placeholder="Ваше пожелание..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  resize: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  // Разрешаем выделение текста и iOS long-press menu (copy/paste)
                  WebkitUserSelect: 'text', userSelect: 'text',
                  WebkitTouchCallout: 'default',
                  touchAction: 'auto',
                }}
              />
              <div style={{
                textAlign: 'right',
                fontSize: '12px',
                color: giftMessage.length >= 120 ? '#f44336' : '#999',
                marginTop: '4px'
              }}>
                {giftMessage.length}/140
              </div>
            </div>
          )}

          {error && (
            <div className="checkout-error">{error}</div>
          )}

          {useBonuses && deliveryPrice > 0 && userBonuses >= (subtotal + serviceFee + (hasPostcard ? postcardPrice : 0) + deliveryPrice) && (
            <label style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              fontSize: 14, lineHeight: 1.4, cursor: 'pointer', color: '#2E7D32',
              padding: '10px 12px', background: '#E8F5E9', borderRadius: 8,
              margin: '8px 0',
            }}>
              <input
                type="checkbox"
                checked={useBonusesForDelivery}
                onChange={(e) => setUseBonusesForDelivery(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary-color)' }}
              />
              <span>Списать доставку со счёта {BRAND.name}</span>
            </label>
          )}

          <OrderSummary
            stemsSubtotal={stemsSubtotal}
            stemsServiceFee={stemsServiceFee}
            bouquetsSubtotalFinal={bouquetsSubtotalFinal}
            bouquetsSubtotalBase={bouquetsSubtotalBase}
            bouquetServiceFee={bouquetServiceFee}
            bouquetCount={(cart || []).filter(it => it.kind === 'bouquet').reduce((s, it) => s + (it.quantity || 0), 0)}
            deliveryPrice={deliveryPrice}
            bonusDiscount={bonusDiscount + (useBonusesForDelivery && deliveryPrice > 0 && userBonuses >= (subtotal + serviceFee + (hasPostcard ? postcardPrice : 0) + deliveryPrice) ? deliveryPrice : 0)}
            totalWithDelivery={useBonusesForDelivery && userBonuses >= (subtotal + serviceFee + (hasPostcard ? postcardPrice : 0) + deliveryPrice) ? 0 : totalWithDelivery}
            bonusesEarned={bonusesEarned}
            postcardPrice={hasPostcard ? postcardPrice : 0}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', margin: '14px 0 6px' }}>
            <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', color: '#333' }}>
              <input
                type="checkbox"
                checked={consentMarketing}
                onChange={(e) => setConsentMarketing(e.target.checked)}
                style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary-color)' }}
              />
              <span>Согласен получать рекламные и информационные сообщения</span>
            </label>
            <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', color: '#333' }}>
              <input
                type="checkbox"
                checked={consentPd}
                onChange={(e) => setConsentPd(e.target.checked)}
                style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary-color)' }}
              />
              <span>
                Я принимаю{' '}
                <a href={legal.policyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1A73E8' }}>
                  Политику конфиденциальности
                </a>
                {' '}и даю согласие на обработку персональных данных
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="checkout-submit"
            disabled={isLoading || isWebGuest || !consentPd || !name.trim() || (deliveryCalc && deliveryCalc.requires_negotiation)}
            style={isWebGuest ? { background: '#E0E0E0', color: '#888', cursor: 'not-allowed' } : undefined}
            title={isWebGuest ? 'Сначала авторизуйтесь через SMS' : (!consentPd ? 'Необходимо принять Политику конфиденциальности' : (!name.trim() ? 'Укажите имя заказчика' : undefined))}
          >
            {isLoading ? 'Оформление...'
              : isWebGuest ? 'Авторизуйтесь для оформления'
              : (deliveryCalc && deliveryCalc.requires_negotiation) ? 'Требуется согласование'
              : !name.trim() ? 'Укажите имя заказчика'
              : 'Оформить заказ'}
          </button>

          <p style={{
            fontSize: '10px', color: '#666', textAlign: 'center',
            margin: '8px 0 0', lineHeight: '1.4',
          }}>
            Нажимая «Оформить заказ», вы принимаете условия{' '}
            <a
              href={legal.ofertaUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#666', textDecoration: 'underline' }}
            >
              Публичной оферты
            </a>
          </p>
        </form>

    </div>
  );
}

export default CheckoutModal;
